import type { FunctionComponent, PointerEvent } from 'react';
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Center,
  Group,
  Loader,
  Slider,
  Stack,
  Text,
} from '@mantine/core';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { getEnhancePhotoErrorCode } from 'api/photos/useEnhancePhoto';
import { FEATURE_FLAGS } from 'constants/chownow';
import { trackMenuPhotoEnhanceClicked } from 'helpers/menuAnalytics';
import { useTranslation } from 'i18n';

import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';
import { CheckIconSm, WarningIconSm } from '@chownow/pantry/icons';

const NS = 'menuPhotosPhotoDrawer';

const ZOOM_MIN = 0;
const ZOOM_MAX = 100;
const ZOOM_DEFAULT = 50;
const ROTATE_MIN = -180;
const ROTATE_MAX = 180;
const ROTATE_DEFAULT = 0;
const MIN_CROP_SIZE = 20;

type Corner = 'tl' | 'tr' | 'bl' | 'br';

export type CropBox = { left: number; top: number; size: number };

export type CropState = {
  zoom: number;
  rotate: number;
  cropBox: CropBox;
};

const DEFAULT_CROP: CropBox = { left: 15, top: 15, size: 70 };

type DragState =
  | { mode: 'move'; startX: number; startY: number; startCrop: CropBox }
  | { mode: 'resize'; corner: Corner; oppX: number; oppY: number };

// Slider midpoint (50) = 1× (natural fit). Left half zooms out to 0.5×; right half zooms in to 3×.
export function zoomToScale(zoom: number): number {
  if (zoom <= 50) {
    return 0.5 + (zoom / 50) * 0.5;
  }
  return 1 + ((zoom - 50) / 50) * 2;
}

type EditPhotoContentProps = {
  imageUrl: string;
  menuId?: string;
  itemId?: string;
  companyId?: string;
  onEnhance?: () => Promise<string>;
  onEnhanceReset?: () => void;
  onReplace: () => void;
  onPreview: (state: CropState) => void;
  initialState?: CropState;
  initialEnhancedUrl?: string | null;
};

export const EditPhotoContent: FunctionComponent<EditPhotoContentProps> = ({
  imageUrl,
  menuId,
  itemId,
  companyId,
  onEnhance,
  onEnhanceReset,
  onReplace,
  onPreview,
  initialState,
  initialEnhancedUrl,
}) => {
  const { t } = useTranslation(NS);
  // Known enhance-failure codes mapped to a specific, actionable message. Any other
  // code (or a non-API error, e.g. network failure) falls back to the generic message.
  const enhanceErrorMessages: Record<string, string> = {
    not_menu_item: t('EditPhotoContent.enhanceErrorNotMenuItem'),
  };
  const flags = useFlags();
  const showEnhance = !!flags?.[FEATURE_FLAGS.menuPhotoEnhancementTool];
  const [zoom, setZoom] = useState(initialState?.zoom ?? ZOOM_DEFAULT);
  const [rotate, setRotate] = useState(initialState?.rotate ?? ROTATE_DEFAULT);
  const [cropBox, setCropBox] = useState<CropBox>(initialState?.cropBox ?? DEFAULT_CROP);
  const [imageReady, setImageReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [enhancedUrl, setEnhancedUrl] = useState<string | null>(initialEnhancedUrl ?? null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [enhanceError, setEnhanceError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const displayUrl = enhancedUrl ?? imageUrl;

  const [prevDisplayUrl, setPrevDisplayUrl] = useState(displayUrl);
  if (prevDisplayUrl !== displayUrl) {
    setPrevDisplayUrl(displayUrl);
    setImageReady(false);
  }

  const handleEnhance = async () => {
    if (!onEnhance || isEnhancing) return;
    trackMenuPhotoEnhanceClicked({ menuId, itemId, companyId });
    setIsEnhancing(true);
    setEnhanceError(null);
    try {
      const url = await onEnhance();
      setEnhancedUrl(url);
    } catch (error) {
      const errorCode = getEnhancePhotoErrorCode(error);
      setEnhanceError(
        (errorCode && enhanceErrorMessages[errorCode]) || t('EditPhotoContent.enhanceError'),
      );
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleReset = () => {
    setZoom(ZOOM_DEFAULT);
    setRotate(ROTATE_DEFAULT);
    setCropBox(DEFAULT_CROP);
    setEnhancedUrl(null);
    setEnhanceError(null);
    onEnhanceReset?.();
  };

  const toPercent = (clientX: number, clientY: number) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: ((clientX - rect.left) / rect.width) * 100,
      y: ((clientY - rect.top) / rect.height) * 100,
    };
  };

  const handleContainerPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = toPercent(e.clientX, e.clientY);
    dragRef.current = { mode: 'move', startX: x, startY: y, startCrop: { ...cropBox } };
    setIsDragging(true);
  };

  const handleCornerPointerDown = (corner: Corner, e: PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    containerRef.current?.setPointerCapture(e.pointerId);
    const { left, top, size } = cropBox;
    const oppX = corner === 'tl' || corner === 'bl' ? left + size : left;
    const oppY = corner === 'tl' || corner === 'tr' ? top + size : top;
    dragRef.current = { mode: 'resize', corner, oppX, oppY };
    setIsDragging(true);
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const { x: mX, y: mY } = toPercent(e.clientX, e.clientY);

    if (drag.mode === 'move') {
      const { startX, startY, startCrop } = drag;
      const { size } = startCrop;
      setCropBox({
        left: Math.max(0, Math.min(100 - size, startCrop.left + (mX - startX))),
        top: Math.max(0, Math.min(100 - size, startCrop.top + (mY - startY))),
        size,
      });
    } else {
      const { corner, oppX, oppY } = drag;
      const newSize = Math.max(MIN_CROP_SIZE, Math.max(Math.abs(mX - oppX), Math.abs(mY - oppY)));
      let newLeft = corner === 'tr' || corner === 'br' ? oppX : oppX - newSize;
      let newTop = corner === 'bl' || corner === 'br' ? oppY : oppY - newSize;
      newLeft = Math.max(0, Math.min(100 - newSize, newLeft));
      newTop = Math.max(0, Math.min(100 - newSize, newTop));
      const clampedSize = Math.min(newSize, 100 - newLeft, 100 - newTop);
      setCropBox({ left: newLeft, top: newTop, size: clampedSize });
    }
  };

  const handlePointerUp = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  const scale = zoomToScale(zoom);
  const { left: cL, top: cT, size: cS } = cropBox;

  const CORNERS: { corner: Corner; x: number; y: number; cursor: string }[] = [
    { corner: 'tl', x: cL, y: cT, cursor: 'nwse-resize' },
    { corner: 'tr', x: cL + cS, y: cT, cursor: 'nesw-resize' },
    { corner: 'bl', x: cL, y: cT + cS, cursor: 'nesw-resize' },
    { corner: 'br', x: cL + cS, y: cT + cS, cursor: 'nwse-resize' },
  ];

  const previewButton = (
    <Button
      fullWidth
      radius="xl"
      tt="uppercase"
      bg="var(--surface-inverse-primary)"
      c="var(--content-inverse-primary)"
      style={{ letterSpacing: 'var(--letterspacing-wide)' }}
      onClick={() => onPreview({ zoom, rotate, cropBox })}
      disabled={isEnhancing}
    >
      {t('EditPhotoContent.preview')}
    </Button>
  );

  return (
    <DockedDrawerFormContent stacked dockButtons={previewButton}>
      <Stack gap="md">
        {enhanceError && (
          <Alert variant="caution" icon={<WarningIconSm />}>
            {enhanceError}
          </Alert>
        )}
        <Box
          ref={containerRef}
          style={{
            position: 'relative',
            aspectRatio: '4/3',
            borderRadius: 'var(--mantine-radius-sm)',
            overflow: 'hidden',
            background: 'var(--surface-secondary)',
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
          }}
          onPointerDown={handleContainerPointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
        >
          <img
            src={displayUrl}
            alt=""
            draggable={false}
            onLoad={() => setImageReady(true)}
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: `scale(${scale}) rotate(${rotate}deg)`,
              transformOrigin: 'center',
              pointerEvents: 'none',
              opacity: imageReady ? 1 : 0,
            }}
          />
          {!imageReady && (
            <Center style={{ position: 'absolute', inset: 0 }}>
              <Loader size="md" color="var(--content-secondary)" />
            </Center>
          )}
          {isEnhancing && (
            <Box
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                pointerEvents: 'none',
              }}
            />
          )}
          {!isEnhancing && (
            <>
              {/* Crop box: shadow darkens surround, dashed border marks crop */}
              <Box
                style={{
                  position: 'absolute',
                  left: `${cL}%`,
                  top: `${cT}%`,
                  width: `${cS}%`,
                  height: `${cS}%`,
                  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
                  border: '1px dashed rgba(255, 255, 255, 0.8)',
                  pointerEvents: 'none',
                }}
              />
              {CORNERS.map(({ corner, x, y, cursor }) => (
                <Box
                  key={corner}
                  style={{
                    position: 'absolute',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    background: 'white',
                    transform: 'translate(-50%, -50%)',
                    left: `${x}%`,
                    top: `${y}%`,
                    cursor,
                    zIndex: 1,
                  }}
                  onPointerDown={e => handleCornerPointerDown(corner, e)}
                />
              ))}
            </>
          )}
        </Box>

        <ButtonGroup>
          {showEnhance && (
            <Button
              variant="ghost"
              flex={1}
              h="xl"
              onClick={() => void handleEnhance()}
              disabled={!onEnhance || isEnhancing}
              leftSection={!isEnhancing && enhancedUrl ? <CheckIconSm /> : undefined}
            >
              {isEnhancing ? (
                <Loader size="xs" color="var(--content-secondary)" />
              ) : (
                t('EditPhotoContent.enhance')
              )}
            </Button>
          )}
          <Button variant="ghost" flex={1} h="xl" onClick={handleReset} disabled={isEnhancing}>
            {t('EditPhotoContent.reset')}
          </Button>
          <Button variant="ghost" flex={1} h="xl" onClick={onReplace} disabled={isEnhancing}>
            {t('EditPhotoContent.replace')}
          </Button>
        </ButtonGroup>

        {/* Zoom slider */}
        <Group gap="xs" align="center" wrap="nowrap">
          <Text variant="caption-xs" tt="uppercase" w={48} style={{ flexShrink: 0 }} aria-hidden>
            {t('EditPhotoContent.zoom')}
          </Text>
          <Slider
            flex={1}
            min={ZOOM_MIN}
            max={ZOOM_MAX}
            value={zoom}
            onChange={setZoom}
            label={null}
            color="var(--surface-inverse-primary)"
            thumbLabel={t('EditPhotoContent.zoom')}
          />
        </Group>

        {/* Rotate slider */}
        <Group gap="xs" align="center" wrap="nowrap">
          <Text variant="caption-xs" tt="uppercase" w={48} style={{ flexShrink: 0 }} aria-hidden>
            {t('EditPhotoContent.rotate')}
          </Text>
          <Slider
            flex={1}
            min={ROTATE_MIN}
            max={ROTATE_MAX}
            value={rotate}
            onChange={setRotate}
            label={null}
            color="var(--surface-inverse-primary)"
            thumbLabel={t('EditPhotoContent.rotate')}
          />
        </Group>
      </Stack>
    </DockedDrawerFormContent>
  );
};
